import { usDay } from "../../../server/rules/week";
import { foldAllButtons, foldCards } from "../../ui/fold";
import type { PatientRecord } from "../../../server/modules/homecare/patients";
import type { ActivityLine } from "../../../server/rules/activity";
import type { ScreenContext } from "../../shell/types";
import { activityPanel } from "../../ui/activity";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { messagesCard } from "../../ui/messages";
import { openModal } from "../../ui/modal";
import { card, chip, ib, muted, table } from "../../ui/record";
import { sendButton } from "../../ui/send";
import { uploadControl } from "../../ui/upload";
import { openViewer } from "../../ui/viewer";
import { archiveModal, area, field, refuse, select } from "../clients/forms";
import { assessmentTab } from "./assessment";
import { authorizationsCard, carePlanTab, holdBanner } from "./careplan";
import { nextStepsCard } from "./nextsteps";
import { whoPaysCard } from "./whopays";
import { patientVisitsTab } from "./visits";
import { patientBillingTab, seesBilling } from "../billing/patient";
import { homeRoutineTab } from "./routine";
import { photoImg } from "../../ui/face";
import { fileItModal } from "./fileit";
import { listLink } from "../../ui/list-link";
import {
  addDiagnosisModal,
  claimDiagnosesModal,
  diagnosisNoteModal,
  onClaimsWords,
} from "./diagnoses";

// The patient record (brief 1C-1, mockup caseRec): the head with the avatar and episode, the
// stage of the episode's workflow folded under "show steps", the step strip (Intake, Patient and
// payer, Assessment, Care plan, Visits, Billing and payroll), and the tabs. Intake, Patient and
// payer and Assessment (brief 1C-2, app/screens/patients/assessment.ts) and Care plan (brief 1C-3,
// app/screens/patients/careplan.ts, with Admission on top and the Authorizations card on Patient
// and payer) and Visits (brief 1D-1, app/screens/patients/visits.ts) are live; Billing and payroll
// says which brief brings it. A hold shows
// loudly under the head on every tab. Under the tabs: Messages, Documents, Contacts and History.

type View = PatientRecord;
type Res = {
  ok: boolean;
  status: number;
  body: View & {
    error?: string;
    flagged?: { level: "flag" | "block"; message: string } | null;
    ask?: string;
  };
};
type Tab = "intake" | "patient" | "assess" | "plan" | "visits" | "billing" | "routine" | "docs";

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const kv = (rows: [string, Node | string][]) =>
  el("dl", { class: "kv" }, ...rows.flatMap(([k, v]) => [el("dt", {}, k), el("dd", {}, v)]));

/** The County field (brief 1D-6): filled from the ZIP, "from ZIP" under it, changed like any
 * other field; what the office types is kept. */
/** Texts OK (brief 1E-19): on by default; off, no texts go to the patient. */
function textsOkField(on: boolean) {
  const input = el("input", { type: "checkbox", id: "pt-texts-ok", checked: on !== false });
  return {
    el: el("label", { class: "ptask", for: "pt-texts-ok" }, input, " Texts OK"),
    input,
  };
}

function countyField(county: string | null, from: string | null) {
  const f = field("County", county ?? "", { maxlength: "60", placeholder: "From the ZIP" });
  if (from) f.el.append(el("span", { class: "small muted" }, from));
  return f;
}

/** The AI marker on a field it filled: "Read by AI, check it". */
const aiMark = () =>
  el("span", { class: "chip info small", "data-ai": "1" }, "Read by AI, check it");

export async function patientRecord(host: HTMLElement, ctx: ScreenContext, id: string) {
  const page = el("div", { class: "record" });
  // Every card on the patient record folds; each person's choice is kept (brief 1E-25).
  // Brief 1E-32 (mockup v90): every card starts folded; the stage line and the parts strip
  // aren't cards and stay as they are.
  foldCards(page, "patient", { openFirst: [] });
  host.append(page);
  const first = (await api<View>("GET", `/api/patients/${encodeURIComponent(id)}`)) as Res;
  if (!first.ok) {
    page.append(
      el(
        "div",
        { class: "blank", role: "alert" },
        icon("circle-slash", "big"),
        el("h2", {}, "Not available"),
        el(
          "p",
          { class: "muted" },
          "This patient isn't here, or opening their record needs a permission.",
        ),
      ),
    );
    return;
  }
  let view: View = first.body;
  const activity = activityPanel("patient", id, { autosaved: false });
  host.append(activity.el);
  ctx.crumb(view.patient.name);

  let tab: Tab = view.episode ? "patient" : "intake";
  let lower: "messages" | "docs" | "contacts" | "history" = view.messages ? "messages" : "docs";
  // What AI read from the referral packet, waiting for a person to check and save.
  let aiPatient: Record<string, string> = {};
  let aiAnswers: Record<string, string> = {};
  const readingOn = ctx.me.ai.reading === "on";
  const messages = view.messages
    ? messagesCard("patient", id, () => void activity.refresh())
    : null;
  // The Assessment tab keeps its own state (brief 1C-2); it's drawn once, the first time it opens.
  const assessSlot = el("div", { "aria-label": "Assessment" });
  let assessLoaded = false;
  // The Care plan tab and the Authorizations card keep their own state too (brief 1C-3).
  const careSlot = el("div");
  let care: Promise<{ reload: () => Promise<void> }> | null = null;
  const authSlot = el("div");
  let auths: Promise<{ reload: () => Promise<void> }> | null = null;
  // The Next steps card under Authorizations (brief 1C-4): orders, what's due, appointments.
  const nextSlot = el("div");
  let next: Promise<{ reload: () => Promise<void> }> | null = null;
  // Who pays (brief 1E-19): the patient's contracts and By default, with the feature on.
  const paysSlot = el("div");
  let pays: Promise<void> | null = null;
  // The Visits tab (brief 1D-1) keeps its own state too.
  const visitsSlot = el("div");
  // Billing and payroll (brief 1E-1): read again each time the tab opens.
  const billingSlot = () => {
    const slot = el("div", { class: "ws" });
    void patientBillingTab(slot, ctx, id);
    return slot;
  };
  let visits: Promise<{ reload: () => Promise<void> }> | null = null;
  // Home and routine (brief 1E-17) keeps its own state too.
  const routineSlot = el("div");
  let routine: Promise<{ reload: () => Promise<void> }> | null = null;
  let routineOpen = false;
  /** Visits were scheduled, changed or cancelled: the record and the plan read themselves again. */
  const visitsChanged = () => {
    void send("GET", "");
    void care?.then((c) => c.reload());
    void auths?.then((a) => a.reload());
  };
  /** Something on the care plan or its authorizations changed: the record reads itself again. */
  const careChanged = () => {
    void send("GET", "");
    void auths?.then((a) => a.reload());
    void next?.then((n) => n.reload());
  };
  /** An order or appointment changed on Next steps: the record and the plan read themselves again. */
  const nextChanged = () => {
    void send("GET", "");
    void care?.then((c) => c.reload());
    // An appointment added from Next steps shows on the Assessment tab next time it opens.
    assessLoaded = false;
  };

  async function took(res: Res): Promise<Res> {
    if (res.ok) {
      view = res.body;
      if (page.isConnected) draw();
      await activity.refresh();
    }
    return res;
  }
  const send = async (method: string, path: string, body?: unknown) =>
    took((await api<View>(method, `/api/patients/${id}${path}`, body)) as Res);

  // ---- The parts of the head ------------------------------------------------------------------------

  function head(): HTMLElement {
    const p = view.patient;
    const line = [
      view.client?.name,
      view.clientType?.name,
      view.episode ? `episode ${view.episode.number}` : "referral",
    ]
      .filter(Boolean)
      .join(" · ");
    const setup = view.client
      ? (() => {
          const b = el(
            "button",
            { type: "button", class: "btn" },
            icon("organization"),
            " Client setup",
          );
          b.addEventListener("click", () => ctx.go(`/homecare/clients/${view.client?.id}`));
          return b;
        })()
      : null;
    const archive =
      view.canEdit && !p.archived
        ? ib("archive", "Archive this patient", () =>
            archiveModal(p.name, `/api/patients/${id}/archive`, () => send("GET", "")),
          )
        : null;
    return el(
      "div",
      { class: "head" },
      el(
        "div",
        { class: "t" },
        el("div", { class: "eyebrow" }, "Patient"),
        el(
          "h1",
          {},
          p.photoFileId
            ? photoImg(`/api/patients/${id}/photos/${p.photoFileId}`, "", "pav")
            : el("span", { class: "pav", "aria-hidden": "true" }, initials(p.name)),
          p.name,
        ),
        el("div", { class: "muted" }, line),
      ),
      el(
        "div",
        { class: "actions" },
        ...([archive, setup].filter((x) => x !== null) as HTMLElement[]),
        // Brief 1E-32: every card on the record at once.
        ...foldAllButtons(page),
      ),
    );
  }

  /** The stage of the episode's workflow, folded (mockup "Stage 8 of 10: In care · show steps"). */
  function stageFold(): HTMLElement {
    const st = view.stage;
    const names = st?.names ?? [];
    const cur = st?.cur ?? 0;
    const next = names[cur + 1];
    const move =
      (way: "next" | "back", confirm = false) =>
      async () => {
        const res = await send("POST", "/stage", { way, confirm });
        // Complete with something missing says what, and asks first (brief 1E-25).
        if (!res.ok && res.status === 409 && res.body.ask) return askFirst(res.body.ask);
        if (!res.ok) toast("The stage didn't change", res.body.error);
        else if (res.body.flagged)
          toast(
            res.body.flagged.level === "block"
              ? "Moved on · blocked, bill or pay held"
              : "Moved on · flagged",
            res.body.flagged.message,
          );
      };
    const live = view.canEdit && !!view.episode && view.episode.status !== "closed";
    const on =
      live && next
        ? el("button", { type: "button", class: "btn pri" }, `Complete · move to ${next}`)
        : null;
    on?.addEventListener("click", move("next"));
    const askFirst = (ask: string) => {
      const yes = el(
        "button",
        { type: "button", class: "btn pri" },
        `Move to ${next ?? ""} anyway`,
      );
      const m = openModal({
        title: `Complete ${names[cur] ?? ""}?`,
        body: [el("p", {}, ask)],
        actions: [yes],
      });
      yes.addEventListener("click", async () => {
        yes.disabled = true;
        m.close();
        await move("next", true)();
      });
    };
    const back = live && cur > 0 ? ib("arrow-right", "Step back", move("back")) : null;
    back?.classList.add("flip");
    const details = el(
      "details",
      { class: "stagefold" },
      el(
        "summary",
        {},
        icon("workflow"),
        ` Stage ${cur + 1} of ${names.length}: `,
        el("b", {}, names[cur] ?? ""),
        // The stage line says what it's waiting for, or how it moved (brief 1E-25).
        st?.waiting ? el("span", {}, " ", chip(`Waiting: ${st.waiting}`, "warn")) : null,
        st?.tail ? el("span", { class: "small muted" }, ` · ${st.tail}`) : null,
        el("span", { class: "small muted" }, " · show steps"),
      ),
      el(
        "section",
        { class: "card stagebox", "aria-label": "Stage" },
        el(
          "div",
          { class: "cardhead", style: "margin:0" },
          el(
            "div",
            {},
            el(
              "div",
              { class: "eyebrow" },
              `${st?.workflow ?? "Episode of care"}${st?.version ? ` · version ${st.version}` : ""}`,
            ),
            el("h2", {}, names[cur] ?? ""),
          ),
          back,
        ),
        el(
          "ol",
          { class: "stepper" },
          ...names.map((s, i) =>
            el(
              "li",
              { class: i < cur ? "done" : i === cur ? "cur" : undefined },
              el("span", { class: "bar" }),
              el("span", { class: "lab" }, el("span", { class: "n" }, String(i + 1)), s),
            ),
          ),
        ),
        checklistBox(),
        el("div", { class: "nextstep" }, st?.hint ? el("p", {}, st.hint) : null, on),
      ),
    );
    return details;
  }

  /**
   * The stage's checklist (brief 1E-25, mockup v77): each item that applies, Done, Waiting (the app
   * checks it) or Confirm (a person, with a note or a file when it asks).
   */
  function checklistBox(): HTMLElement | null {
    const c = view.stage.checklist;
    if (!c || !c.items.length) return null;
    const done = c.items.filter((i) => i.state === "done").length;
    const rows = c.items.map((i) => {
      const lead =
        i.state === "done"
          ? chip("Done", "ok")
          : i.state === "not_available"
            ? chip("Not available", "plain")
            : i.state === "waiting"
              ? chip("Waiting", "plain")
              : view.canEdit
                ? (() => {
                    const b = el(
                      "button",
                      { type: "button", class: "btn sm" },
                      icon("check"),
                      " Confirm",
                    );
                    b.addEventListener("click", () => confirmItem(i));
                    return b;
                  })()
                : chip("To confirm", "warn");
      const meta = [i.owner, i.within ? `within ${i.within}` : null, i.optional ? "optional" : null]
        .filter(Boolean)
        .join(" · ");
      return el(
        "div",
        { class: "prow", "data-item": i.name },
        el(
          "span",
          {},
          lead,
          " ",
          el("b", {}, i.name),
          meta ? el("span", { class: "small muted" }, ` · ${meta}`) : null,
          i.note ? el("span", { class: "small muted" }, ` · ${i.note}`) : null,
        ),
        chip(i.how === "app" ? "App checks" : "Person confirms", i.how === "app" ? "ok" : "info"),
      );
    });
    return el(
      "div",
      { class: "checklist", "aria-label": "Stage checklist" },
      el(
        "div",
        { class: "cardhead", style: "margin:10px 0 4px" },
        el(
          "div",
          {},
          el(
            "h3",
            { class: "h3s", style: "margin:0" },
            `${c.stage} · ${done} of ${c.items.length}`,
          ),
          c.next ? el("span", { class: "small muted" }, `To move to ${c.next}`) : null,
        ),
      ),
      ...rows,
    );
  }

  function confirmItem(i: NonNullable<View["stage"]["checklist"]>["items"][number]) {
    const note = area(i.proof === "note" ? "Note" : "Note (optional)", "");
    const pick = select(
      "File",
      view.files.map((f) => [f.id, f.name] as const),
      view.files[0]?.id ?? "",
    );
    const yes = el("button", { type: "button", class: "btn pri" }, icon("check"), " Confirm");
    const m = openModal({
      title: i.name,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          note.el,
          i.proof === "file" ? pick.el : null,
        ),
      ],
      actions: [yes],
    });
    yes.addEventListener("click", async () => {
      yes.disabled = true;
      const res = await send("POST", `/checklist/${i.id}`, {
        note: note.input.value,
        fileId: i.proof === "file" ? pick.input.value : undefined,
      });
      yes.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast("Confirmed", "In the history");
    });
  }

  /** The step strip (mockup .parts.six): each part opens its tab. */
  function strip(): HTMLElement {
    const r = view.referral;
    const a = view.admission;
    type Dot = "ok" | "warn" | "bad" | "plain";
    const steps: [Tab, string, string, Dot][] = [
      [
        "intake",
        "Intake",
        // Done only when the required answers are saved (brief 1E-25).
        !r
          ? "–"
          : view.intake.done
            ? `Done${view.intake.doneOn ? ` ${view.intake.doneOn.slice(0, 5)}` : ""}`
            : !r.firstContact && r.cameAs === "new"
              ? "First contact due"
              : `${view.intake.unanswered} answer${view.intake.unanswered === 1 ? "" : "s"} missing`,
        view.intake.done ? "ok" : "warn",
      ],
      ["patient", "Patient and payer", view.payer.kind ?? "–", view.payer.saved ? "ok" : "warn"],
      [
        "assess",
        "Assessment",
        view.assessment?.words ?? "Not started",
        !view.assessment || view.assessment.words === "Not started"
          ? view.episode
            ? "bad"
            : "plain"
          : view.assessment.signed
            ? "ok"
            : "warn",
      ],
      [
        "plan",
        "Care plan",
        view.hold
          ? "On hold"
          : view.plan
            ? view.plan.status === "signed"
              ? view.plan.length
              : "Draft"
            : a.admitted
              ? "Admitted"
              : view.episode
                ? "Admission due"
                : "Not yet",
        view.hold
          ? "warn"
          : view.plan?.status === "signed"
            ? "ok"
            : view.plan
              ? "warn"
              : a.admitted
                ? "ok"
                : "plain",
      ],
      [
        "visits",
        "Visits",
        view.visits.total ? `${view.visits.thisWeek} this week` : "None yet",
        view.visits.thisWeek ? "ok" : "plain",
      ],
      [
        "billing",
        "Billing and payroll",
        a.billingHeld
          ? "Held · who pays"
          : view.episode
            ? "Who pays confirmed"
            : (view.client?.name ?? "–"),
        a.billingHeld ? "bad" : view.episode ? "ok" : "plain",
      ],
    ];
    const parts: Node[] = [];
    steps.forEach(([t, l, sub, k], i) => {
      if (i)
        parts.push(el("span", { class: "parr", "aria-hidden": "true" }, icon("chevron-right")));
      const b = el(
        "button",
        { type: "button", class: `part${tab === t ? " on" : ""}`, "data-part": t },
        el("span", { class: "pn" }, String(i + 1)),
        el("span", {}, el("b", {}, l), el("small", {}, sub)),
        k !== "plain" ? el("i", { class: `pdot2 ${k}`, "aria-hidden": "true" }) : null,
      );
      b.addEventListener("click", () => {
        tab = t;
        draw();
      });
      parts.push(b);
    });
    return el("div", { class: "parts six", "aria-label": "Steps" }, ...parts);
  }

  function tabs(): HTMLElement {
    const list: [Tab, string][] = [
      ["intake", "Intake"],
      ["patient", "Patient and payer"],
      ["assess", "Assessment"],
      ["plan", "Care plan"],
      ["visits", `Visits ${view.visits.total}`],
      ["billing", "Billing and payroll"],
      ["routine", "Home and routine"],
      ["docs", `Documents ${view.files.length}`],
    ];
    return el(
      "div",
      { class: "tabs tabs2", role: "tablist" },
      ...list.map(([t, l]) => {
        const b = el(
          "button",
          { type: "button", role: "tab", "aria-selected": String(tab === t) },
          l,
        );
        b.addEventListener("click", () => {
          tab = t;
          draw();
        });
        return b;
      }),
    );
  }

  // ---- Intake ---------------------------------------------------------------------------------------

  function referralCard(): HTMLElement {
    const r = view.referral;
    if (!r) return card("Referral", null, [], muted("No referral on file."));
    const existing = r.cameAs === "existing";
    const logged = r.firstContact
      ? el("span", {}, chip(r.firstContact, "ok"), r.firstContactBy ? ` ${r.firstContactBy}` : "")
      : existing
        ? chip("No response clock · existing patient", "plain")
        : el(
            "span",
            {},
            chip(r.late ? "Not yet · past the response window" : "Not yet", "warn"),
            r.due ? el("span", { class: "small muted" }, ` due by ${r.due}`) : "",
          );
    const log =
      view.canEdit && !r.firstContact && !existing
        ? (() => {
            const b = el(
              "button",
              { type: "button", class: "btn sm pri" },
              icon("check"),
              " Log first contact",
            );
            b.addEventListener("click", () => logContact());
            return b;
          })()
        : null;
    // The pencil (brief 1E-23): change the first contact, or add a later call to it.
    const pencil =
      view.canEdit && r.firstContact
        ? ib("pencil", "Change first contact", () => changeContact())
        : null;
    const up = view.canEdit
      ? uploadControl({
          kind: "patient",
          id,
          label: "Upload",
          onUploaded: async (f) => {
            await send("GET", "");
            fileAfterUpload(f, "referral_packet");
          },
        }).el
      : null;
    return el(
      "section",
      { class: "card", "aria-label": "Referral" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("div", { class: "eyebrow" }, view.intake.form?.name ?? "Intake"),
          el("h2", {}, "Referral"),
        ),
        el("span", { class: "rowacts" }),
      ),
      cameAsRow(),
      kv([
        ["From", view.client?.name ?? "–"],
        ["How it came in", r.sourceLabel],
        existing && r.onServiceSince
          ? ["On service since", usDay(r.onServiceSince)]
          : ["Received", r.received],
        ...(existing && r.firstPlanFrom
          ? ([["First care plan in FlowCare from", usDay(r.firstPlanFrom)]] as [string, string][])
          : []),
        ["First contact", el("span", {}, logged, pencil ? " " : "", pencil ?? "")],
        ...(r.firstContactWith ? ([["With", r.firstContactWith]] as [string, string][]) : []),
        ...(r.firstContactNote ? ([["Note", r.firstContactNote]] as [string, string][]) : []),
        ["Response window", view.careRules.response],
      ]),
      r.added.length
        ? el(
            "div",
            { class: "small", style: "margin:4px 0 8px", "aria-label": "Added to first contact" },
            ...r.added.map((n) => el("div", {}, icon("note"), ` ${n.on} · ${n.note}`)),
          )
        : el("span"),
      log ? el("div", { class: "actions", style: "margin-top:8px" }, log) : el("span"),
      el(
        "div",
        { class: "cardhead", style: "margin:10px 0 4px" },
        el("h3", { class: "h3s", style: "margin:0" }, "Came with"),
        up ? el("span", { class: "rowbtns" }, up) : el("span"),
      ),
      r.came.length
        ? el(
            "div",
            { class: "linkrow" },
            ...r.came.map((f) => {
              const b = el(
                "button",
                { type: "button", class: "chip plain", "data-tip": "Open in the viewer" },
                f.name,
              );
              b.addEventListener("click", () => void openViewer({ file: f }));
              return b;
            }),
          )
        : muted("No documents yet."),
    );
  }

  /**
   * Came in as (brief 1E-25, mockup v85): a new referral, or an existing patient with On service
   * since and the date the first FlowCare care plan starts. An existing patient has no response
   * clock.
   */
  function cameAsRow(): HTMLElement {
    const r = view.referral;
    if (!r) return el("span");
    const name = `came-${id}`;
    const radio = (v: "new" | "existing", words: string) => {
      const i = el("input", { type: "radio", name, value: v, checked: r.cameAs === v });
      if (!view.canEdit) i.disabled = true;
      return { i, el: el("label", { class: "tsr" }, i, ` ${words}`) };
    };
    const n = radio("new", "New referral");
    const x = radio("existing", "Existing patient");
    const since = field("On service since", r.onServiceSince ?? "", { type: "date" });
    const from = field("First care plan in FlowCare from", r.firstPlanFrom ?? view.today, {
      type: "date",
    });
    const dates = el(
      "div",
      { class: "grid2", style: "gap:10px;margin-top:6px" },
      since.el,
      from.el,
    );
    const save = el("button", { type: "button", class: "btn sm" }, icon("check"), " Save");
    const show = () => {
      const ex = x.i.checked;
      dates.hidden = !ex;
      save.hidden = !view.canEdit || (!ex && r.cameAs === "new");
    };
    n.i.addEventListener("change", show);
    x.i.addEventListener("change", show);
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await send("PUT", "/came-as", {
        cameAs: x.i.checked ? "existing" : "new",
        onServiceSince: since.input.value,
        firstPlanFrom: from.input.value,
      });
      save.disabled = false;
      if (!res.ok) return toast("Not saved", res.body.error);
      toast("Saved", x.i.checked ? "No response clock for an existing patient" : "");
    });
    const box = el(
      "div",
      { class: "field", style: "margin:0 0 10px" },
      el("label", {}, "Came in as"),
      el("div", { class: "linkrow" }, n.el, x.el),
      dates,
      el("div", { class: "actions", style: "margin-top:6px" }, save),
    );
    show();
    return box;
  }

  /** The pencil on a saved answer (brief 1E-25): the new answer and the reason, kept in its history. */
  function changeAnswer(q: View["intake"]["questions"][number]) {
    const { lid, input } = answerInput(q, q.answer);
    const why = field("Reason for the change", "", {
      maxlength: "300",
      placeholder: "e.g. Family corrected it",
    });
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const m = openModal({
      title: `Change ${q.label}`,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el("div", { class: "field" }, el("label", { for: lid }, q.label), input),
          why.el,
        ),
      ],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await send("PUT", "/intake", {
        answers: { [q.key]: input.value === "" ? null : input.value },
        reason: why.input.value,
      });
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast("Answer saved", "Kept in its history");
    });
  }

  /** Change first contact (mockup fcModal): when, with whom, the note, and what's added to it. */
  function changeContact() {
    const r = view.referral;
    if (!r) return;
    const when = field("When", r.firstContactAt ?? "", { placeholder: "MM/DD/YYYY 10:15 AM" });
    const withWho = field("With", r.firstContactWith ?? "", { maxlength: "160" });
    const note = area("Note", r.firstContactNote ?? "");
    const add = area("Add to it", "");
    add.input.placeholder = "A later call or a correction";
    const why = field("Reason for the change", "", {
      maxlength: "300",
      placeholder: "e.g. Note typed wrong",
    });
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const m = openModal({
      title: "Change first contact",
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el("div", { class: "grid2", style: "gap:10px" }, when.el, withWho.el),
          note.el,
          add.el,
          why.el,
        ),
      ],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await send("PATCH", "/first-contact", {
        at: when.input.value,
        with: withWho.input.value,
        note: note.input.value,
        add: add.input.value,
        reason: why.input.value,
      });
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast("First contact saved", "Kept in History with the earlier note");
    });
  }

  /** Right after an upload: File it, with what the place it came from says it likely is. */
  function fileAfterUpload(f: { id: string; name: string }, preset: string | null) {
    fileItModal({ id: f.id, name: f.name }, view.fileKinds, () => void send("GET", ""), preset);
  }

  function logContact() {
    const note = area("What was said (optional)", "");
    const go = el("button", { type: "button", class: "btn pri" }, icon("check"), " Log it");
    const m = openModal({
      title: "First contact",
      sub: "Marks the time the referral source was reached.",
      body: [el("div", { class: "form", style: "max-width:none" }, note.el)],
      actions: [go],
    });
    note.input.focus();
    go.addEventListener("click", async () => {
      go.disabled = true;
      const res = await send("POST", "/first-contact", { note: note.input.value });
      go.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast("First contact logged");
    });
  }

  /** One input per question, by its answer type. */
  function answerInput(q: View["intake"]["questions"][number], value: unknown) {
    const lid = `q-${q.key.replace(/\./g, "-")}`;
    let input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    if (q.answerType === "yes_no")
      input = el(
        "select",
        { id: lid },
        el("option", { value: "" }, "–"),
        el("option", { value: "yes", selected: value === true || value === "yes" }, "Yes"),
        el("option", { value: "no", selected: value === false || value === "no" }, "No"),
      );
    else if (q.answerType === "choice")
      input = el(
        "select",
        { id: lid },
        el("option", { value: "" }, "–"),
        ...q.options.map((o) => el("option", { value: o, selected: value === o }, o)),
      );
    else if (q.answerType === "text")
      input = el("textarea", { id: lid, rows: "2" }, value == null ? "" : String(value));
    else
      input = el("input", {
        id: lid,
        type: q.answerType === "date" ? "date" : "number",
        value: value == null ? "" : String(value),
      });
    return { lid, input };
  }

  function answersCard(): HTMLElement {
    const qs = view.intake.questions;
    if (!qs.length)
      return card(
        "Intake answers",
        "The questions come from the client type",
        [],
        muted("This client type's intake form asks nothing yet (Settings › Intake questions)."),
      );
    const inputs = new Map<string, HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>();
    const marked = new Set<string>();
    const rows: Node[] = qs.map((q) => {
      const fromAi = aiAnswers[q.key];
      // A saved answer reads as it is, with a pencil and its last change (brief 1E-25).
      if (q.answer !== null && q.answer !== "" && fromAi === undefined) {
        const last = q.history[0];
        return el(
          "div",
          { class: "field wide", "data-answer": q.key },
          el(
            "label",
            {},
            q.label,
            el("span", { class: "small muted" }, ` · saved on the ${q.savedOn}`),
          ),
          el(
            "div",
            { class: "linkrow" },
            el("span", {}, q.words),
            view.canEdit ? ib("pencil", `Change ${q.label}`, () => changeAnswer(q)) : null,
          ),
          last && last.was !== null
            ? el(
                "span",
                { class: "small muted" },
                icon("history"),
                ` Changed ${last.on} by ${last.by}${last.reason ? ` · ${last.reason}` : ""} (was ${last.was})`,
              )
            : null,
        );
      }
      const { lid, input } = answerInput(q, fromAi ?? q.answer);
      inputs.set(q.key, input);
      if (fromAi !== undefined) marked.add(q.key);
      if (!view.canEdit) input.setAttribute("disabled", "");
      input.addEventListener("input", () => {
        if (marked.has(q.key)) input.closest(".field")?.querySelector("[data-ai]")?.remove();
      });
      return el(
        "div",
        { class: "field wide" },
        el(
          "label",
          { for: lid },
          q.label,
          el("span", { class: "small muted" }, ` · saved on the ${q.savedOn}`),
        ),
        input,
        fromAi !== undefined ? aiMark() : null,
      );
    });
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save intake");
    save.addEventListener("click", async () => {
      const answers: Record<string, unknown> = {};
      for (const [k, i] of inputs) answers[k] = i.value === "" ? null : i.value;
      save.disabled = true;
      // The fields AI filled that a person kept, for the history.
      const kept = qs.filter(
        (q) => marked.has(q.key) && inputs.get(q.key)?.value === aiAnswers[q.key],
      );
      const res = await send("PUT", "/intake", {
        answers,
        aiChecked: kept.map((q) => q.label),
      });
      save.disabled = false;
      if (!res.ok) return toast("Intake wasn't saved", res.body.error);
      aiAnswers = {};
      toast("Intake saved");
    });
    const actions: Node[] = [];
    if (view.canEdit && readingOn) actions.push(readButton());
    return el(
      "section",
      { class: "card", "aria-label": "Intake answers" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, "Intake answers"),
          el(
            "span",
            { class: "small muted" },
            `The questions come from the client type${view.intake.form ? ` · ${view.intake.form.name}, version ${view.intake.form.version}` : ""}`,
          ),
        ),
        el("span", { class: "rowacts" }, ...actions),
      ),
      el(
        "div",
        { class: "form", style: "max-width:none" },
        el("div", { class: "formgrid" }, ...rows),
      ),
      view.canEdit ? el("div", { class: "actions", style: "margin-top:10px" }, save) : el("span"),
    );
  }

  /** Read the referral packet (AI on): fills intake and patient fields for a person to check. */
  function readButton(): HTMLElement {
    return ib("sparkles-fill", "Read the referral packet", async () => {
      const files = view.referral?.came.length ? view.referral.came : view.files;
      const packet = files[0];
      if (!packet) return toast("Nothing to read yet", "Upload the referral packet first");
      toast("Reading the referral packet…");
      const res = await api<{
        patient: Record<string, string>;
        answers: Record<string, string>;
        read: string[];
      }>("POST", "/api/ai/packet", { patient: id, file: packet.id });
      if (!res.ok) return toast("AI couldn't read it", res.body.error);
      aiPatient = res.body.patient;
      aiAnswers = res.body.answers;
      draw();
      toast(
        `AI filled ${res.body.read.length} answer${res.body.read.length === 1 ? "" : "s"}`,
        "Check each marked answer, then save.",
      );
    });
  }

  // ---- Patient and payer ------------------------------------------------------------------------------

  /** The patient's fields as a form (New: with Save patient and payer; Edit: in a modal). */
  function patientFields(fromAi: Record<string, string> = {}) {
    const p = view.patient;
    const v = (k: string, had: string | null) => fromAi[k] ?? had ?? "";
    const mk = (
      label: string,
      k: string,
      had: string | null,
      attrs: Record<string, string> = {},
    ) => {
      const f = field(label, v(k, had), attrs);
      if (fromAi[k] !== undefined) f.el.append(aiMark());
      return f;
    };
    const f = {
      firstName: mk("First name", "firstName", p.firstName, { maxlength: "60" }),
      // Brief 1E-28: a standard field; box 2 prints Last, First, MI.
      middleInitial: field("Middle initial", p.middleInitial, { maxlength: "1" }),
      lastName: mk("Last name", "lastName", p.lastName, { maxlength: "60" }),
      birthDate: mk("Date of birth", "birthDate", p.birthDate, { type: "date" }),
      sex: select("Sex", [["", "–"], ...view.sexes.map((s) => [s.value, s.label] as const)], p.sex),
      phone: mk("Phone", "phone", p.phone, { type: "tel", maxlength: "20" }),
      language: mk("Language", "language", p.language, { maxlength: "60" }),
      addressLine1: mk("Street", "addressLine1", p.address.addressLine1, { maxlength: "120" }),
      addressLine2: field("Apartment, floor", p.address.addressLine2, { maxlength: "120" }),
      city: mk("City", "city", p.address.city, { maxlength: "60" }),
      state: mk("State", "state", p.address.state, { maxlength: "2" }),
      zip: mk("ZIP", "zip", p.address.zip, { maxlength: "10", inputmode: "numeric" }),
      county: countyField(p.county, p.countyFrom),
      emergencyName: field("Emergency contact", p.emergency.name, { maxlength: "120" }),
      emergencyRelation: field("How they're related", p.emergency.relation, { maxlength: "60" }),
      emergencyPhone: field("Emergency contact's phone", p.emergency.phone, { type: "tel" }),
      primaryCare: mk("Primary care", "primaryCare", p.primaryCare, { maxlength: "160" }),
      allergies: mk("Allergies", "allergies", p.allergies, { maxlength: "300" }),
      codeStatus: select(
        "Code status",
        [["", "–"], ...view.codeStatuses.map((c) => [c, c] as const)],
        p.codeStatus,
      ),
      cameFrom: field("Came from", p.cameFrom, { maxlength: "200" }),
      textsOk: textsOkField(p.textsOk),
    };
    const values = () => ({
      firstName: f.firstName.input.value,
      middleInitial: f.middleInitial.input.value,
      lastName: f.lastName.input.value,
      birthDate: f.birthDate.input.value,
      sex: f.sex.input.value || null,
      phone: f.phone.input.value,
      language: f.language.input.value,
      addressLine1: f.addressLine1.input.value,
      addressLine2: f.addressLine2.input.value,
      city: f.city.input.value,
      state: f.state.input.value,
      zip: f.zip.input.value,
      county: f.county.input.value,
      emergencyName: f.emergencyName.input.value,
      emergencyRelation: f.emergencyRelation.input.value,
      emergencyPhone: f.emergencyPhone.input.value,
      primaryCare: f.primaryCare.input.value,
      allergies: f.allergies.input.value,
      codeStatus: f.codeStatus.input.value,
      cameFrom: f.cameFrom.input.value,
      textsOk: f.textsOk.input.checked,
    });
    const keptFromAi = () =>
      Object.entries(fromAi)
        .filter(
          ([k, val]) =>
            (f as unknown as Record<string, { input: HTMLInputElement }>)[k]?.input.value === val,
        )
        .map(
          ([k]) =>
            (f as unknown as Record<string, { el: HTMLElement }>)[k]?.el.querySelector("label")
              ?.textContent ?? k,
        );
    const grid = el("div", { class: "formgrid" }, ...Object.values(f).map((x) => x.el));
    return { grid, values, keptFromAi };
  }

  /** One input (or a few, for a contact person) per payer field, by its kind. */
  function payerInputs() {
    const read: (() => [string, unknown])[] = [];
    const els: HTMLElement[] = [];
    for (const pf of view.payer.fields) {
      const label = `${pf.label}${pf.required ? "" : " (optional)"}`;
      if (pf.kind === "contact") {
        const c = (pf.value ?? {}) as { name?: string; phone?: string; email?: string };
        const name = field(`${label}: name`, c.name ?? "", { maxlength: "120" });
        const phone = field(`${pf.label}: phone`, c.phone ?? "", { type: "tel" });
        const email = field(`${pf.label}: email`, c.email ?? "", { type: "email" });
        els.push(name.el, phone.el, email.el);
        read.push(() => [
          pf.key,
          name.input.value.trim()
            ? { name: name.input.value, phone: phone.input.value, email: email.input.value }
            : null,
        ]);
      } else if (pf.kind === "yes_no") {
        const s = select(
          label,
          [
            ["", "–"],
            ["yes", "Yes"],
            ["no", "No"],
          ],
          pf.value === true ? "yes" : pf.value === false ? "no" : "",
        );
        els.push(s.el);
        read.push(() => [pf.key, s.input.value || null]);
      } else if (pf.kind === "choice") {
        const s = select(
          label,
          [["", "–"], ...pf.options.map((o) => [o, o] as const)],
          String(pf.value ?? ""),
        );
        els.push(s.el);
        read.push(() => [pf.key, s.input.value || null]);
      } else if (pf.kind === "file") {
        const s = select(
          label,
          [["", "–"], ...view.files.map((x) => [x.id, x.name] as const)],
          String(pf.value ?? ""),
        );
        els.push(s.el);
        read.push(() => [pf.key, s.input.value || null]);
      } else {
        const f = field(label, pf.value == null ? "" : String(pf.value), {
          type: pf.kind === "date" ? "date" : pf.kind === "number" ? "number" : "text",
          maxlength: "200",
        });
        els.push(f.el);
        read.push(() => [pf.key, f.input.value || null]);
      }
    }
    return { els, values: () => Object.fromEntries(read.map((r) => r())) };
  }

  /** Before episode 1: one form, Save patient and payer. */
  function patientAndPayerForm(): HTMLElement {
    const pf = patientFields(aiPatient);
    const pay = payerInputs();
    const save = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("check"),
      " Save patient and payer",
    );
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await send("POST", "/payer", {
        patient: pf.values(),
        fields: pay.values(),
        aiChecked: pf.keptFromAi(),
      });
      save.disabled = false;
      if (!res.ok) return toast("That wasn't saved", res.body.error);
      aiPatient = {};
      toast(
        `Episode ${res.body.episode?.number ?? 1} started`,
        "Billing is held until who pays is confirmed.",
      );
    });
    return el(
      "section",
      { class: "card", "aria-label": "Patient and payer" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("div", { class: "eyebrow" }, view.payer.kind ?? "Patient and payer"),
          el("h2", {}, "Patient and payer"),
          el(
            "span",
            { class: "small muted" },
            "Saving both starts episode 1 on the client's workflow.",
          ),
        ),
      ),
      el(
        "div",
        { class: "form", style: "max-width:none" },
        el("h3", { class: "h3s" }, "Patient"),
        pf.grid,
        el("h3", { class: "h3s" }, `Who pays · ${view.payer.kind ?? ""}`),
        el("div", { class: "formgrid" }, ...pay.els),
      ),
      el("div", { class: "actions", style: "margin-top:10px" }, save),
    );
  }

  function editPatient() {
    const pf = patientFields();
    const save = el("button", { type: "button", class: "btn pri" }, "Save");
    const m = openModal({
      title: `Edit ${view.patient.name}`,
      sub: "A new address goes on the map when it's saved.",
      body: [el("div", { class: "form", style: "max-width:none" }, pf.grid)],
      actions: [save],
      wide: true,
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await send("PUT", "", pf.values());
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast("Patient saved");
    });
  }

  function editPayer() {
    const pay = payerInputs();
    const save = el("button", { type: "button", class: "btn pri" }, "Save");
    const m = openModal({
      title: "Who pays",
      sub: `${view.payer.kind ?? ""}: the details this patient was saved with.`,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el("div", { class: "formgrid" }, ...pay.els),
          // Brief 1E-28: the fields come from the client type (Settings › Lists › Payer fields).
          listLink("payer_fields", "Payer details for each client type"),
        ),
      ],
      actions: [save],
      wide: true,
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await send("POST", "/payer", { fields: pay.values() });
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast("Who pays saved");
    });
  }

  function addDiagnosis() {
    addDiagnosisModal(id, () => send("GET", ""));
  }

  function patientCard(): HTMLElement {
    const p = view.patient;
    const acts: Node[] = view.canEdit ? [ib("pencil", "Edit patient", editPatient)] : [];
    const dx = view.diagnoses.length
      ? el(
          "div",
          {},
          ...view.diagnoses.map((d) =>
            el(
              "div",
              { class: "small dxrow" },
              el(
                "div",
                { style: "display:flex;gap:6px;align-items:center;flex-wrap:wrap" },
                el("b", {}, d.code),
                ` ${d.description}`,
                d.primary ? chip("Primary", "info") : "",
                view.canEdit
                  ? ib("pencil", `Note on ${d.code}`, () =>
                      diagnosisNoteModal(id, d, () => send("GET", "")),
                    )
                  : "",
                view.canEdit
                  ? ib("x", `Take off ${d.code}`, () =>
                      archiveModal(
                        `${d.code} ${d.description}`,
                        `/api/patients/${id}/diagnoses/${d.id}/archive`,
                        () => send("GET", ""),
                      ),
                    )
                  : "",
              ),
              d.note ? el("div", { class: "muted" }, d.note) : null,
            ),
          ),
        )
      : muted("None yet.");
    // Brief 1E-28: which diagnoses each client's claims print in box 21 (all until picked).
    const picks = view.claimDiagnoses;
    const mine = picks.find((x) => x.clientId === view.client?.id) ?? picks[0];
    const onClaims =
      view.diagnoses.length && picks.length
        ? (() => {
            const b = el(
              "button",
              { type: "button", class: "btn sm ghost", "data-dxc": "1" },
              onClaimsWords(mine),
            );
            if (view.canEdit)
              b.addEventListener("click", () =>
                claimDiagnosesModal(id, picks, view.diagnoses, view.client?.id ?? null, () =>
                  send("GET", ""),
                ),
              );
            else b.disabled = true;
            return b;
          })()
        : null;
    return el(
      "section",
      { class: "card", "aria-label": "Patient" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          { style: "display:flex;gap:12px;align-items:center" },
          el("span", { class: "pav big", "aria-hidden": "true" }, initials(p.name)),
          el(
            "div",
            {},
            el("div", { class: "eyebrow" }, view.clientType?.name ?? "Home Care"),
            el("h2", { style: "margin:0" }, "Patient"),
          ),
        ),
        el("span", { class: "rowacts" }, ...acts),
      ),
      kv([
        ["Date of birth", p.born],
        ["Address", p.addressLine ?? "–"],
        // County beside the address (brief 1D-6): where it came from in small text; none is
        // flagged, never hidden.
        [
          "County",
          p.county
            ? el(
                "span",
                { "data-county": "1" },
                p.county,
                p.countyFrom ? el("span", { class: "small muted" }, ` ${p.countyFrom}`) : "",
              )
            : chip(p.noCounty ?? "No county yet", "warn"),
        ],
        ["Phone", p.phone ?? "–"],
        // Brief 1E-19: off, the patient gets no texts.
        ["Texts OK", p.textsOk ? "Yes" : "No"],
        ["Language", p.language ?? "–"],
        [
          "Emergency contact",
          p.emergency.name
            ? [
                p.emergency.name,
                p.emergency.relation ? `(${p.emergency.relation})` : "",
                p.emergency.phone ? `· ${p.emergency.phone}` : "",
              ]
                .filter(Boolean)
                .join(" ")
            : "–",
        ],
        ["Primary care", p.primaryCare ?? "–"],
        ["Allergies", p.allergies ?? "–"],
        ["Code status", p.codeStatus ?? "–"],
        ["Came from", p.cameFrom ?? "–"],
      ]),
      el(
        "div",
        { class: "cardhead", style: "margin:12px 0 6px" },
        el("h3", { class: "h3s", style: "margin:0" }, "Diagnoses"),
        el(
          "span",
          { class: "rowacts" },
          onClaims ?? "",
          view.canEdit ? ib("plus", "Add a diagnosis", addDiagnosis) : "",
        ),
      ),
      dx,
    );
  }

  function payerCard(): HTMLElement {
    const acts: Node[] = [];
    if (view.canEdit && view.payer.saved) acts.push(ib("pencil", "Edit who pays", editPayer));
    if (view.client) {
      const b = el("button", { type: "button", class: "btn sm" }, view.client.name);
      b.addEventListener("click", () => ctx.go(`/homecare/clients/${view.client?.id}`));
      acts.push(b);
    }
    return el(
      "section",
      { class: "card", "aria-label": "Who pays" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("div", { class: "eyebrow" }, view.payer.kind ?? ""),
          el("h2", {}, "Who pays"),
        ),
        el("span", { class: "rowacts" }, ...acts),
      ),
      kv([
        ...view.payer.fields.map((f) => [f.label, f.words] as [string, string]),
        [
          "Financial responsibility",
          view.payer.confirmed
            ? chip(`Confirmed by ${view.payer.confirmed.by}`, "ok")
            : chip("Not confirmed yet · billing held", "warn"),
        ],
      ]),
    );
  }

  function billCard(): HTMLElement {
    const b = view.billing as unknown as Record<string, string>;
    return el(
      "section",
      { class: "card billto", "aria-label": "Billing and collection" },
      el(
        "div",
        { class: "cardhead" },
        el("h2", {}, icon("credit-card"), " Billing and collection"),
        el(
          "span",
          { class: "small muted" },
          "Comes from part 1. The care plan sets what's billed.",
        ),
      ),
      kv([
        ["Bill to", b.billTo ?? "–"],
        ["Format", b.billsAs ?? "–"],
        ["Billed by", b.unit ?? "–"],
        ["On every invoice", b.onInvoice ?? "–"],
        ["Terms", b.terms ?? "–"],
        ["Collect", b.collect ?? "–"],
      ]),
    );
  }

  function episodesCard(): HTMLElement {
    return el(
      "section",
      { class: "card", "aria-label": "Episodes of care" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("history"), " Episodes of care"),
          el(
            "span",
            { class: "small muted" },
            "A new episode starts over: new assessment, new care plan.",
          ),
        ),
      ),
      view.episodes.length
        ? table(
            [
              { h: "Episode", f: (e) => el("b", {}, String(e.number)) },
              { h: "From", f: (e) => e.from },
              {
                h: "To",
                f: (e) =>
                  e.to
                    ? e.to
                    : chip(
                        e.status === "closed" ? "Closed" : "Current",
                        e.status === "closed" ? "plain" : "ok",
                      ),
              },
              { h: "Why", f: (e) => e.why },
            ],
            view.episodes,
            undefined,
            "Episodes of care",
          )
        : muted("No episode yet. Saving patient and payer starts episode 1."),
    );
  }

  // ---- Care plan: admission ---------------------------------------------------------------------------

  function admissionCard(): HTMLElement {
    const a = view.admission;
    if (!view.episode)
      return card(
        el("span", {}, icon("sign-in"), " Admission"),
        "Save patient and payer first: that starts episode 1.",
        [],
        el("span"),
      );
    if (a.admitted)
      return el(
        "div",
        { class: "admbar", role: "status" },
        icon("check-circle"),
        el(
          "span",
          {},
          "Admitted · start of care ",
          el(
            "b",
            {},
            a.startOfCare
              ? `${a.startOfCare.slice(5, 7)}/${a.startOfCare.slice(8, 10)}/${a.startOfCare.slice(0, 4)}`
              : "",
          ),
          ` · by ${a.admitted.by}`,
        ),
      );
    // Only files filed as signed consents are offered (brief 1E-23), and the one picked before.
    const consentFiles = view.files.filter(
      (f) => f.kind?.showsIn === "consents" || f.id === a.consentsFile?.id,
    );
    const consents = select(
      "Signed consents (the file)",
      [
        ["", consentFiles.length ? "Pick the signed consents" : "Upload the signed consents"],
        ...consentFiles.map((f) => [f.id, f.name] as const),
      ],
      a.consentsFile?.id ?? "",
    );
    const signedOn = field("Signed on", a.consentsSignedOn ?? view.today, { type: "date" });
    const who = field(
      "Who is financially responsible",
      a.financialResponsibility ?? view.client?.name ?? "",
      { maxlength: "200" },
    );
    const soc = field("Start of care", a.startOfCare ?? "", { type: "date" });
    const confirm = el("input", {
      type: "checkbox",
      id: "adm-who",
      checked: !!view.payer.confirmed,
      disabled: !!view.payer.confirmed,
    });
    const up = uploadControl({
      kind: "patient",
      id,
      label: "Upload signed consents",
      camera: true,
      fileKind: view.fileKinds.find((k) => k.showsIn === "consents")?.id,
      onUploaded: async (f) => {
        await send("GET", "");
        // The new file is picked for the consents.
        const sel = page.querySelector<HTMLSelectElement>("[data-consents] select");
        if (sel) sel.value = f.id;
      },
    });
    const save = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("check"),
      " Save admission",
    );
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await send("POST", "/admission", {
        ...(consents.input.value
          ? { consentsFileId: consents.input.value, consentsSignedOn: signedOn.input.value }
          : {}),
        financialResponsibility: who.input.value,
        startOfCare: soc.input.value || null,
        confirmWhoPays: confirm.checked || undefined,
      });
      save.disabled = false;
      if (!res.ok) return toast("Admission wasn't saved", res.body.error);
      toast(
        res.body.admission.admitted ? "Admitted" : "Admission saved",
        res.body.admission.billingHeld
          ? "Billing stays held until who pays is confirmed"
          : "Billing hold cleared",
      );
    });
    return el(
      "section",
      { class: "card", "aria-label": "Admission" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("div", { class: "eyebrow" }, `Episode ${view.episode.number}`),
          el("h2", {}, icon("sign-in"), " Admission"),
          el(
            "span",
            { class: "small muted" },
            "Consents signed and who pays confirmed. Visits before then are flagged.",
          ),
        ),
      ),
      el(
        "div",
        { class: "linkrow", style: "margin-bottom:8px" },
        a.careFlagged
          ? chip("Care flagged: admission not complete", "warn")
          : chip("Admission complete", "ok"),
        a.billingHeld
          ? chip("Billing held: who pays not confirmed", "bad")
          : chip("Billing not held", "ok"),
      ),
      el(
        "div",
        { class: "form", style: "max-width:none" },
        el(
          "div",
          { class: "formgrid" },
          el("div", { "data-consents": "1" }, consents.el),
          signedOn.el,
          el("div", { class: "wide" }, up.el),
          who.el,
          soc.el,
          el(
            "div",
            { class: "field wide" },
            el(
              "label",
              { for: "adm-who", style: "display:flex;gap:8px;align-items:center" },
              confirm,
              "Who pays is confirmed (financial responsibility)",
            ),
          ),
        ),
      ),
      view.canEdit ? el("div", { class: "actions", style: "margin-top:10px" }, save) : el("span"),
    );
  }

  // ---- Documents --------------------------------------------------------------------------------------

  function filesTable(withUpload: boolean): HTMLElement {
    const up =
      withUpload && view.canEdit
        ? uploadControl({
            kind: "patient",
            id,
            camera: true,
            onUploaded: async (f) => {
              await send("GET", "");
              fileAfterUpload(f, null);
            },
          }).el
        : null;
    return el(
      "div",
      {},
      view.files.length
        ? table(
            [
              {
                h: "File",
                f: (f) => {
                  const b = el(
                    "button",
                    { type: "button", class: "btn sm ghost" },
                    icon("file"),
                    ` ${f.name}`,
                  );
                  b.addEventListener(
                    "click",
                    () =>
                      void openViewer({
                        file: { id: f.id, name: f.name },
                        onDone: () => void send("GET", ""),
                      }),
                  );
                  return b;
                },
              },
              // What it is (brief 1E-23): a file with no kind is Not filed until it's filed.
              {
                h: "What it is",
                f: (f) =>
                  f.kind
                    ? el("span", {}, f.kind.name, f.datedOn ? muted(` · ${f.datedOn}`) : "")
                    : f.docType
                      ? f.docType
                      : chip("Not filed", "warn"),
              },
              {
                h: "",
                num: true,
                f: (f) =>
                  el(
                    "span",
                    { class: "rowbtns" },
                    view.canEdit
                      ? ib("pencil", "File it: what it is", () =>
                          fileItModal(
                            { id: f.id, name: f.name, kindId: f.kind?.id, datedOn: f.datedOn },
                            view.fileKinds,
                            () => void send("GET", ""),
                          ),
                        )
                      : "",
                    sendButton({
                      files: [{ id: f.id, name: f.name }],
                      document: f.kind?.name ?? f.docType ?? f.name,
                    }),
                  ),
              },
            ],
            view.files,
            undefined,
            "Documents",
          )
        : muted("No documents yet."),
      up ? el("div", { style: "margin-top:10px" }, up) : el("span"),
    );
  }

  // ---- Under the tabs: Messages, Documents, Contacts, History ----------------------------------------

  async function lowerCard(): Promise<HTMLElement> {
    const contacts = [
      ...(view.patient.emergency.name
        ? [
            {
              who: view.patient.emergency.name,
              role: `Emergency contact${view.patient.emergency.relation ? ` (${view.patient.emergency.relation})` : ""}`,
              reach: view.patient.emergency.phone ?? "–",
            },
          ]
        : []),
      // Brief 1E-28: the care team members kept in Contacts.
      ...view.careTeamContacts,
      ...view.payer.fields
        .filter((f) => f.kind === "contact" && f.value)
        .map((f) => {
          const c = f.value as { name: string; phone: string | null; email: string | null };
          return {
            who: c.name,
            role: f.label,
            reach: [c.phone, c.email].filter(Boolean).join(" · ") || "–",
          };
        }),
    ];
    let history: ActivityLine[] = [];
    if (lower === "history") {
      const res = await api<{ lines: ActivityLine[] }>("GET", `/api/activity/patient/${id}`);
      history = res.ok ? res.body.lines : [];
    }
    const tabs: ["messages" | "docs" | "contacts" | "history", string, string][] = [
      ...(messages ? [["messages", "comment", "Messages"] as ["messages", string, string]] : []),
      ["docs", "file", `Documents ${view.files.length}`],
      ["contacts", "people", `Contacts ${contacts.length}`],
      ["history", "history", "History"],
    ];
    const bar = el(
      "div",
      { class: "tabs", role: "tablist" },
      ...tabs.map(([k, ic, l]) => {
        const b = el(
          "button",
          { type: "button", role: "tab", "aria-selected": String(lower === k) },
          icon(ic),
          ` ${l}`,
        );
        b.addEventListener("click", () => {
          lower = k;
          void drawLower();
        });
        return b;
      }),
    );
    let body: Node;
    if (lower === "messages" && messages) body = messages;
    else if (lower === "docs") body = filesTable(false);
    else if (lower === "contacts")
      body = contacts.length
        ? table(
            [
              { h: "Who", f: (c) => el("b", {}, c.who) },
              { h: "Role", f: (c) => c.role },
              { h: "How to reach", f: (c) => c.reach },
            ],
            contacts,
            undefined,
            "Contacts",
          )
        : muted("No contacts yet.");
    else
      body = history.length
        ? el(
            "ul",
            { class: "plist2" },
            ...history
              .slice(0, 30)
              .map((l) =>
                el(
                  "li",
                  {},
                  el("b", {}, l.what),
                  l.detail ? ` · ${l.detail}` : "",
                  el("span", { class: "small muted" }, ` · ${l.who} · ${l.when}`),
                ),
              ),
          )
        : muted("Nothing yet.");
    return el(
      "section",
      { class: "card lowercard", "aria-label": "Messages, documents, contacts and history" },
      bar,
      body,
    );
  }
  const lowerSlot = el("div");
  async function drawLower() {
    lowerSlot.replaceChildren(await lowerCard());
  }

  // ---- The page ---------------------------------------------------------------------------------------

  function body(): Node[] {
    if (tab === "intake") return [el("div", { class: "grid2" }, referralCard(), answersCard())];
    if (tab === "patient") {
      if (!view.episode && view.canEdit) return [patientAndPayerForm(), episodesCard()];
      if (view.episode && !auths) auths = authorizationsCard(authSlot, id, careChanged);
      if (view.episode && !next) next = nextStepsCard(nextSlot, id, nextChanged);
      if (view.episode && !pays) pays = whoPaysCard(paysSlot, id);
      return [
        paysSlot,
        el("div", { class: "grid2" }, patientCard(), el("div", {}, payerCard(), billCard())),
        authSlot,
        nextSlot,
        episodesCard(),
      ];
    }
    if (tab === "assess") {
      if (!view.episode)
        return [
          el(
            "section",
            { class: "card" },
            el(
              "div",
              { class: "blank", style: "padding:20px" },
              icon("checklist", "big"),
              el("h2", {}, "Assessment"),
              el(
                "p",
                { class: "muted" },
                "It starts with episode 1: save patient and payer first.",
              ),
            ),
          ),
        ];
      if (!view.canEdit)
        return [
          el(
            "section",
            { class: "card" },
            el("p", { class: "muted" }, "The Assessment tab needs Patients at Edit."),
          ),
        ];
      if (!assessLoaded) {
        assessLoaded = true;
        void assessmentTab(assessSlot, ctx, id, () => void send("GET", ""));
      }
      return [assessSlot];
    }
    if (tab === "plan") {
      if (!view.episode || !view.canEdit) return [admissionCard()];
      if (!care)
        care = carePlanTab(careSlot, ctx, id, careChanged, (t) => {
          tab = t;
          draw();
        });
      return [admissionCard(), careSlot];
    }
    if (tab === "visits") {
      if (!visits) visits = patientVisitsTab(visitsSlot, id, visitsChanged);
      else void visits.then((v) => v.reload());
      return [visitsSlot];
    }
    if (tab === "routine") {
      // Home and routine (brief 1E-17): read again each time the tab opens; a new photo redraws
      // the head.
      if (!routine) routine = homeRoutineTab(routineSlot, id, () => void send("GET", ""));
      else if (!routineOpen) void routine.then((r) => r.reload());
      routineOpen = true;
      return [routineSlot];
    }
    routineOpen = false;
    if (tab === "billing")
      return [
        el(
          "div",
          { class: `note${view.admission.billingHeld ? " warnnote" : ""}`, role: "status" },
          el(
            "p",
            {},
            view.admission.billingHeld
              ? "Billing is held until who pays is confirmed (Care plan › Admission)."
              : view.episode
                ? "Who pays is confirmed: nothing holds this patient's billing."
                : "Billing starts with episode 1.",
          ),
        ),
        ...(seesBilling(ctx) ? [billingSlot()] : []),
      ];
    return [
      card(
        "Documents",
        "Referral packets, consents and anything else on file",
        [],
        filesTable(true),
      ),
    ];
  }

  function draw() {
    page.replaceChildren(
      head(),
      ...(view.patient.archived
        ? [
            el(
              "p",
              { class: "note", role: "status" },
              `Archived: ${view.patient.archiveReason ?? ""}`,
            ),
          ]
        : []),
      ...(view.hold
        ? [
            holdBanner(view.hold, id, view.canEdit, () => {
              void send("GET", "");
              void care?.then((c) => c.reload());
            }),
          ]
        : []),
      stageFold(),
      strip(),
      tabs(),
      ...body(),
      lowerSlot,
    );
    void drawLower();
  }

  draw();
}
